var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Checkbox, Flex, Link, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DropdownBase, FormButton, GenericError, Icon, MarkdownBase, NmibleFieldRenderer, PageWrapper } from '@/components';
import { NmibleService } from '@/services';
import { useConfig, useUI } from '@/store';
import { APP_CONFIG, DataTest, NmibleService as NS, ROUTE } from '@/types';
const { ConfigComponentType, PaymentTermsDocumentType } = APP_CONFIG.COMPONENTS;
const parseFieldErrors = (err) => {
    var _a, _b, _c, _d;
    const detail = (_b = (_a = err.data) === null || _a === void 0 ? void 0 : _a.detail) !== null && _b !== void 0 ? _b : (_d = (_c = err.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.detail;
    if (!Array.isArray(detail))
        return {};
    return Object.fromEntries(detail.map(({ loc, msg }) => [loc === null || loc === void 0 ? void 0 : loc[loc.length - 1], msg]).filter(([key]) => Boolean(key)));
};
const resolveDocumentUrl = (urls, language) => {
    var _a, _b;
    return (_b = (_a = urls[language]) !== null && _a !== void 0 ? _a : urls[language.split('-')[0]]) !== null && _b !== void 0 ? _b : Object.values(urls)[0];
};
export const PaymentSetup = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { getComponentConfig } = useConfig();
    const { withLoading } = useUI();
    const config = getComponentConfig(ConfigComponentType.PAYMENT_SETUP);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [acceptedTerms, setAcceptedTerms] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const visibleTerms = useMemo(() => {
        var _a;
        if (!selectedMethod)
            return [];
        return ((_a = config === null || config === void 0 ? void 0 : config.terms_and_conditions) !== null && _a !== void 0 ? _a : []).filter((doc) => doc.type === PaymentTermsDocumentType.PRIVACY_POLICY ||
            (doc.type === PaymentTermsDocumentType.VIRTUAL_CARD &&
                selectedMethod.payment_type === NS.PaymentDetailsType.VIRTUAL_CARD));
    }, [config === null || config === void 0 ? void 0 : config.terms_and_conditions, selectedMethod]);
    const allTermsAccepted = visibleTerms.every((doc) => acceptedTerms[doc.type]);
    const resetFormState = () => {
        setFormValues({});
        setFieldErrors({});
        setAcceptedTerms({});
    };
    const fetchPaymentMethods = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield withLoading(() => __awaiter(void 0, void 0, void 0, function* () {
                const data = yield NmibleService.getPaymentMethods();
                setPaymentMethods(data);
            }));
        }
        catch (_a) {
            setHasError(true);
        }
    }), [withLoading]);
    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);
    const handleMethodChange = (method) => {
        setSelectedMethod(method);
        resetFormState();
    };
    const handleFieldChange = (propertyName, value) => {
        setFormValues((prev) => (Object.assign(Object.assign({}, prev), { [propertyName]: value })));
        if (fieldErrors[propertyName]) {
            setFieldErrors((prev) => {
                const _a = prev, _b = propertyName, _ = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
                return rest;
            });
        }
    };
    const handleSubmit = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!selectedMethod || !allTermsAccepted)
            return;
        try {
            setIsSubmitting(true);
            setFieldErrors({});
            yield withLoading(() => __awaiter(void 0, void 0, void 0, function* () {
                yield NmibleService.setPaymentMethod({
                    endpoint: selectedMethod.endpoint,
                    payload: Object.assign(Object.assign({}, formValues), { accepted_tc: new Date().toISOString() })
                });
            }));
            navigate(ROUTE.PAYMENT);
        }
        catch (err) {
            const errors = parseFieldErrors(err);
            if (Object.keys(errors).length) {
                setFieldErrors(errors);
            }
            else {
                setHasError(true);
            }
        }
        finally {
            setIsSubmitting(false);
        }
    });
    if (hasError)
        return _jsx(GenericError, {});
    return (_jsxs(PageWrapper, Object.assign({ title: t(config === null || config === void 0 ? void 0 : config.title) }, { children: [_jsx(MarkdownBase, { color: 'darkBrandBlue.90', markdown: t(config === null || config === void 0 ? void 0 : config.content) }), _jsxs(Flex, Object.assign({ direction: 'column', gap: '12', mt: '12' }, { children: [_jsx(DropdownBase, { getOptionLabel: (option) => option.title, getOptionValue: (option) => option.method_provider_name, onChange: handleMethodChange, options: paymentMethods, placeholder: t(config === null || config === void 0 ? void 0 : config.select_payment_type_placeholder), value: selectedMethod }), selectedMethod && (_jsxs(Box, Object.assign({ as: 'form', onSubmit: handleSubmit }, { children: [_jsx(Flex, Object.assign({ flexWrap: 'wrap', gap: '6' }, { children: selectedMethod.properties.map((field) => (_jsx(NmibleFieldRenderer, { field: field, isRequired: selectedMethod.required_fields.includes(field.property_name), onChange: handleFieldChange, value: formValues[field.property_name], errors: fieldErrors }, field.property_name))) })), !!visibleTerms.length && (_jsx(Flex, Object.assign({ direction: 'column', gap: '4', mt: '8' }, { children: visibleTerms.map((doc) => {
                                    const url = resolveDocumentUrl(doc.urls, i18n.language);
                                    return (_jsx(Checkbox, Object.assign({ "data-test": `${DataTest.PAYMENT_SETUP_TC_CHECKBOX}-${doc.type.toLowerCase()}`, isChecked: !!acceptedTerms[doc.type], onChange: (e) => setAcceptedTerms((prev) => (Object.assign(Object.assign({}, prev), { [doc.type]: e.target.checked }))), size: 'lg' }, { children: _jsxs(Flex, Object.assign({ alignItems: 'center', gap: '2' }, { children: [_jsx(Text, { children: t(doc.accept_checkbox_text) }), url && (_jsx(Link, Object.assign({ "data-test": `${DataTest.PAYMENT_SETUP_TC_LINK}-${doc.type.toLowerCase()}`, href: url, isExternal: true, color: 'darkBrandBlue.40', onClick: (e) => e.stopPropagation() }, { children: _jsxs(Flex, Object.assign({ alignItems: 'center', gap: '1' }, { children: [_jsx(Text, Object.assign({ textDecoration: 'underline' }, { children: t(doc.label) })), _jsx(Icon, { name: 'link', p: '0', size: '4', type: 'solid' })] })) })))] })) }), doc.type));
                                }) }))), _jsx(FormButton, Object.assign({ "data-test": DataTest.PAYMENT_SETUP_SUBMIT, mt: '8', type: 'submit', loading: isSubmitting, isDisabled: isSubmitting || !allTermsAccepted }, { children: t(config === null || config === void 0 ? void 0 : config.submit_button_text) }))] })))] }))] })));
};
